import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { createContext, type FC, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { NAVY, RADIUS_ITEM, STATUS_GOOD, YELLOW } from 'styles/tokens';

type FeedbackKind = 'saved' | 'copied';

interface FeedbackState {
  message: string;
  kind: FeedbackKind;
  key: number;
}

interface FeedbackContextValue {
  /** Confirm that an entity (notes, tags, client) was saved successfully. */
  notifySaved: (message?: string) => void;
  /** Confirm that some text was copied to the clipboard. */
  notifyCopied: (message?: string) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

const KIND_CONFIG: Record<FeedbackKind, { icon: ReactNode; defaultMessage: string; iconColor: string }> = {
  saved: {
    icon: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />,
    defaultMessage: 'Cambios guardados',
    iconColor: STATUS_GOOD,
  },
  copied: {
    icon: <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />,
    defaultMessage: 'Copiado al portapapeles',
    iconColor: YELLOW,
  },
};

interface FeedbackProviderProps {
  children: ReactNode;
}

export const FeedbackProvider: FC<FeedbackProviderProps> = ({ children }) => {
  const [state, setState] = useState<FeedbackState | null>(null);

  const show = useCallback((kind: FeedbackKind, message?: string) => {
    setState({ kind, message: message ?? KIND_CONFIG[kind].defaultMessage, key: Date.now() });
  }, []);

  const value = useMemo<FeedbackContextValue>(
    () => ({
      notifySaved: (message) => show('saved', message),
      notifyCopied: (message) => show('copied', message),
    }),
    [show]
  );

  const config = state ? KIND_CONFIG[state.kind] : null;

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <Snackbar
        key={state?.key}
        open={!!state}
        autoHideDuration={1800}
        onClose={() => setState(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {state && config ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: NAVY,
              color: '#fff',
              borderRadius: RADIUS_ITEM,
              px: 2,
              py: 1,
              boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.18)',
              minHeight: 40,
            }}
          >
            <Box sx={{ color: config.iconColor, display: 'flex' }}>{config.icon}</Box>
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
              {state.message}
            </Typography>
          </Box>
        ) : (
          <span />
        )}
      </Snackbar>
    </FeedbackContext.Provider>
  );
};

export const useFeedback = (): FeedbackContextValue => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return ctx;
};
